import { useCallback, useEffect, useMemo, useState } from 'react';
import { allTimeZones, deviceTimeZone } from 'fym-shared/src/music24/timeZones';
import { useGetAccountTimeZoneQuery, useUpdateAccountTimeZoneMutation } from '../../../../../redux/api/fymAPI';
import { useSettingsSaveState } from '../../useSettingsSaveState';
import { filterTimeZones } from './timeZoneOptions';

/**
 * The time zone row of one artist as a draft. The picked zone starts at the stored zone, else at
 * the zone of this device (a suggestion only: nothing is saved before the user clicks Save).
 */
export function useTimeZoneDraft(accountId: number) {
  const { data, error, isLoading, refetch } = useGetAccountTimeZoneQuery(
    { accountId },
    // The Plan tab can store the zone of its first viewer: read it again on each visit.
    { refetchOnMountOrArgChange: true }
  );
  const [update, updateState] = useUpdateAccountTimeZoneMutation();
  const save = useSettingsSaveState();
  const stored = data?.timeZone ?? null;
  const device = useMemo(() => deviceTimeZone(), []);
  const initial = stored ?? device ?? 'UTC';
  const [picked, setPicked] = useState(initial);
  const [query, setQuery] = useState('');

  // The stored zone arrives after the first render, and again after a save.
  useEffect(() => setPicked(initial), [initial]);

  const zones = useMemo(() => allTimeZones(), []);
  const { options, matchCount } = useMemo(
    () => filterTimeZones(zones, query, picked, new Date()),
    [picked, query, zones]
  );
  const canSave = !updateState.isLoading && picked !== stored;

  const { setState } = save;
  const submit = useCallback(async (): Promise<void> => {
    setState('saving');
    const result = await update({ accountId, timeZone: picked });
    setState('error' in result ? 'failed' : 'saved');
  }, [accountId, picked, setState, update]);

  return {
    stored,
    device,
    picked,
    setPicked,
    query,
    setQuery,
    options,
    noMatch: matchCount === 0 && query.trim() !== '',
    canSave,
    submit,
    saveState: save.state,
    isSaving: updateState.isLoading,
    loadError: error,
    isLoading,
    refetch,
  };
}
